import React, { useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet, Alert, Platform } from 'react-native';
import type { AlertButton } from 'react-native';
import { Text } from 'react-native-paper';
import { colors, spacing } from '../../constants/theme';
import { adminService } from '../../services/adminService';
import { reviewService } from '../../services/reviewService';
import ShareItemModal from '../../components/ShareItemModal';
import { MAX_PHOTOS_PER_UPLOAD } from '../../constants/photoLimits';
import AdminCategoriesSection from './components/AdminCategoriesSection';
import AdminCumulativeReportsSection from './components/AdminCumulativeReportsSection';
import AdminCreateCategoryModal from './components/AdminCreateCategoryModal';
import AdminCreateItemModal from './components/AdminCreateItemModal';
import AdminDashboardHeader from './components/AdminDashboardHeader';
import AdminEditCategoryModal from './components/AdminEditCategoryModal';
import AdminEditItemModal from './components/AdminEditItemModal';
import AdminItemsSection from './components/AdminItemsSection';
import AdminNewQuestionModal from './components/AdminNewQuestionModal';
import AdminQuestionModal from './components/AdminQuestionModal';
import AdminSharingLinksSection from './components/AdminSharingLinksSection';
import AdminTemplatesSection from './components/AdminTemplatesSection';
import AdminTemplateModal from './components/AdminTemplateModal';
import AdminVendorModal from './components/AdminVendorModal';
import {
  AdminDashboardTab,
  AdminDashboardProps,
  AdminCategoryPayload,
  AdminItemPayload,
  AdminSharingLink,
  CategoryForm,
  ItemForm,
  NewQuestionForm,
  QuestionField,
  ReviewCategory,
  ReviewItem,
  ReviewTemplate,
  TemplateForm,
  TemplateModalMode,
  Vendor,
} from './AdminDashboard.types';
import { findingClassificationForAdminSave } from '../../utils/reviewFindings';

interface ApiErrorLike {
  message?: string;
  response?: {
    data?: {
      message?: string;
    };
  };
}

const DEFAULT_CREATE_CATEGORY_FORM: CategoryForm = {
  name: '',
  description: '',
  icon: 'home',
  parentCategoryId: '',
  showInHamburger: false,
  hamburgerIcon: null,
  hamburgerOrder: 0,
};

const DEFAULT_EDIT_CATEGORY_FORM: CategoryForm = {
  ...DEFAULT_CREATE_CATEGORY_FORM,
  icon: '',
};

const DEFAULT_CREATE_ITEM_FORM: ItemForm = {
  name: '',
  description: '',
  categoryId: '',
  requiresLocation: true,
  requiresFloor: true,
  showInHamburger: false,
  hamburgerIcon: null,
  hamburgerOrder: 0,
  findingMode: 'off',
  findingQuestionIds: [],
};

const DEFAULT_TEMPLATE_FORM: TemplateForm = {
  name: '',
  type: '',
  description: '',
};

const createDefaultNewQuestionForm = (): NewQuestionForm => ({
  label: '',
  type: 'text',
  required: false,
  options: '',
  multiple: false,
  maxPhotos: MAX_PHOTOS_PER_UPLOAD,
  triggersMultiplicity: false,
  links: [{ url: '', label: '' }],
  unit: 'in',
});

const getQuestionFields = (item: ReviewItem): QuestionField[] => (
  Array.isArray(item.questionsSchema?.fields) ? item.questionsSchema.fields : []
);

const getApiErrorMessage = (error: unknown, fallback: string) => {
  if (typeof error !== 'object' || error === null) {
    return fallback;
  }

  const apiError = error as ApiErrorLike;
  return apiError.response?.data?.message || apiError.message || fallback;
};

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ onBack }) => {
  const [items, setItems] = useState<ReviewItem[]>([]);
  const [categories, setCategories] = useState<ReviewCategory[]>([]);
  const [templates, setTemplates] = useState<ReviewTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null); // Will be set after templates load
  const [loading, setLoading] = useState(true);
  const [includeDeactivated, setIncludeDeactivated] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ReviewItem | null>(null);
  const [createItemModalVisible, setCreateItemModalVisible] = useState(false);
  const [createCategoryModalVisible, setCreateCategoryModalVisible] = useState(false);
  const [editItemModalVisible, setEditItemModalVisible] = useState(false);
  const [editCategoryModalVisible, setEditCategoryModalVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<ReviewCategory | null>(null);
  const [activeTab, setActiveTab] = useState<AdminDashboardTab>('items');
  const [allItemLinks, setAllItemLinks] = useState<AdminSharingLink[]>([]);
  
  // Template management state
  const [templateModalVisible, setTemplateModalVisible] = useState(false);
  const [templateModalMode, setTemplateModalMode] = useState<TemplateModalMode>('create');
  const [selectedTemplate, setSelectedTemplate] = useState<ReviewTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState<TemplateForm>(DEFAULT_TEMPLATE_FORM);
  
  // Vendor management state
  const [vendorModalVisible, setVendorModalVisible] = useState(false);
  const [selectedTemplateForVendors, setSelectedTemplateForVendors] = useState<ReviewTemplate | null>(null);
  const [availableVendors, setAvailableVendors] = useState<Vendor[]>([]);
  const [loadingVendors, setLoadingVendors] = useState(false);

  // Sharing state
  const [shareItemModalVisible, setShareItemModalVisible] = useState(false);
  const [selectedItemForSharing, setSelectedItemForSharing] = useState<ReviewItem | null>(null);

  // Form state for modals
  const [createItemForm, setCreateItemForm] = useState<ItemForm>(DEFAULT_CREATE_ITEM_FORM);
  const [createCategoryForm, setCreateCategoryForm] = useState<CategoryForm>(DEFAULT_CREATE_CATEGORY_FORM);
  const [editItemForm, setEditItemForm] = useState<ItemForm>(DEFAULT_CREATE_ITEM_FORM);
  const [editCategoryForm, setEditCategoryForm] = useState<CategoryForm>(DEFAULT_EDIT_CATEGORY_FORM);
  
  // Local state for item ordering in category edit
  const [localItemOrder, setLocalItemOrder] = useState<ReviewItem[]>([]);
  const [hasOrderChanges, setHasOrderChanges] = useState(false);
  
  // State for expanded categories in the admin view
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());

  // Local state for question ordering in modal
  const [localQuestionOrder, setLocalQuestionOrder] = useState<QuestionField[]>([]);
  const [hasQuestionOrderChanges, setHasQuestionOrderChanges] = useState(false);
  
  // Dropdown menu state
  const [createCategoryMenuVisible, setCreateCategoryMenuVisible] = useState(false);
  const [editCategoryMenuVisible, setEditCategoryMenuVisible] = useState(false);
  const [createParentCategoryMenuVisible, setCreateParentCategoryMenuVisible] = useState(false);
  const [editParentCategoryMenuVisible, setEditParentCategoryMenuVisible] = useState(false);
  
  // New question creation state
  const [newQuestionModalVisible, setNewQuestionModalVisible] = useState(false);
  const [newQuestionForm, setNewQuestionForm] = useState<NewQuestionForm>(createDefaultNewQuestionForm);
  
  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    if (selectedTemplateId !== null) {
      loadData();
    }
  }, [includeDeactivated, selectedTemplateId]);

  useEffect(() => {
    if (activeTab === 'sharing' && selectedTemplateId !== null) {
      loadTemplateLinks();
    }
  }, [activeTab, selectedTemplateId]);

  // Initialize edit form when item is selected for editing
  useEffect(() => {
    if (selectedItem && editItemModalVisible) {
      setEditItemForm({
        name: selectedItem.name,
        description: selectedItem.description || '',
        categoryId: selectedItem.categoryId.toString(),
        requiresLocation: selectedItem.requiresLocation ?? true,
        requiresFloor: selectedItem.requiresFloor ?? true,
        showInHamburger: selectedItem.showInHamburger || false,
        hamburgerIcon: selectedItem.hamburgerIcon || null,
        hamburgerOrder: selectedItem.hamburgerOrder || 0,
        findingMode: selectedItem.questionsSchema?.findingClassification?.mode || 'off',
        findingQuestionIds: selectedItem.questionsSchema?.findingClassification?.questionIds || [],
      });
    }
  }, [selectedItem, editItemModalVisible]);

  const loadTemplates = async () => {
    try {
      const templatesResponse = await adminService.listReviewTemplates<ReviewTemplate>();
      // Templates API returns array directly, not wrapped in data property
      const templatesData = Array.isArray(templatesResponse) ? templatesResponse : [];
      setTemplates(templatesData);

      // Set default to first template if not already set
      if (templatesData.length > 0 && selectedTemplateId === null) {
        setSelectedTemplateId(templatesData[0].id);
      } else if (templatesData.length === 0) {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      setTemplates([]);
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [itemsResponse, categoriesResponse] = await Promise.all([
        adminService.listItems<ReviewItem>(includeDeactivated),
        adminService.listCategories<ReviewCategory>()
      ]);
      
      // API now returns unwrapped data from api.ts
      const allItemsData = Array.isArray(itemsResponse) ? itemsResponse : [];
      const allCategoriesData = Array.isArray(categoriesResponse) ? categoriesResponse : [];
      
      // Filter by selected template
      const filteredCategories = allCategoriesData.filter((cat: ReviewCategory) => cat.templateId === selectedTemplateId);
      const categoryIds = filteredCategories.map((cat: ReviewCategory) => cat.id);
      const filteredItems = allItemsData.filter((item: ReviewItem) => categoryIds.includes(item.categoryId));
      
      setItems(filteredItems);
      setCategories(filteredCategories);
      
      // Update selectedItem if it's currently selected to reflect new data
      if (selectedItem) {
        const updatedItem = filteredItems.find((item: ReviewItem) => item.id === selectedItem.id);
        if (updatedItem) {
          setSelectedItem(updatedItem);
        }
      }
      
      // Update selectedCategory if it's currently selected to reflect new data
      if (selectedCategory) {
        const updatedCategory = filteredCategories.find((cat: ReviewCategory) => cat.id === selectedCategory.id);
        if (updatedCategory) {
          setSelectedCategory(updatedCategory);
        }
      }
    } catch (error) {
      console.error('Error loading admin data:', error);
      Alert.alert('Error', 'Failed to load admin data');
      // Set empty arrays on error to prevent undefined issues
      setItems([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplateLinks = async () => {
    if (!selectedTemplateId) return;

    try {
      const links = await reviewService.getTemplateLinks(selectedTemplateId);
      setAllItemLinks(links);
    } catch (error) {
      console.error('Error loading template links:', error);
      Alert.alert('Error', 'Failed to load shared item links');
      setAllItemLinks([]);
    }
  };

  const handleCumulativeReportTemplateUpdated = (updatedTemplate: ReviewTemplate) => {
    setTemplates(current => current.map(template =>
      template.id === updatedTemplate.id ? updatedTemplate : template
    ));
  };

  const handleUnlinkItems = async (linkId: number) => {
    try {
      // Optimistic update - remove from state immediately
      setAllItemLinks(prev => prev.filter(link => link.id !== linkId));

      await reviewService.unlinkItems(linkId);
      Alert.alert('Success', 'Item link removed successfully');
    } catch (error) {
      console.error('Error unlinking items:', error);
      Alert.alert('Error', 'Failed to unlink items');
      // Refetch on error to restore correct state
      loadTemplateLinks();
    }
  };

  const handleDeactivateItem = async (itemId: number) => {
    try {
      await adminService.deprecateItem(itemId);
      Alert.alert('Success', 'Item deactivated successfully');
      loadData();
    } catch (error) {
      console.error('Error deactivating item:', error);
      Alert.alert('Error', 'Failed to deactivate item');
    }
  };

  const handleRestoreItem = async (itemId: number) => {
    try {
      await adminService.restoreItem(itemId);
      Alert.alert('Success', 'Item restored successfully');
      loadData();
    } catch (error) {
      console.error('Error restoring item:', error);
      Alert.alert('Error', 'Failed to restore item');
    }
  };

  const updateQuestionFields = async (
    itemId: number,
    updateFields: (fields: QuestionField[]) => QuestionField[],
    errorLogMessage: string,
    errorAlertMessage: string,
    successMessage?: string,
  ) => {
    try {
      const item = items.find(i => i.id === itemId);
      if (!item) {
        Alert.alert('Error', 'Item not found');
        return;
      }

      const updatedFields = updateFields(getQuestionFields(item));
      const updatedQuestionsSchema = {
        ...item.questionsSchema,
        fields: updatedFields
      };

      await adminService.updateItem(itemId, {
        questionsSchema: updatedQuestionsSchema
      });

      if (modalVisible) {
        setLocalQuestionOrder([...updatedFields]);
      }

      if (selectedItem && selectedItem.id === itemId) {
        setSelectedItem({
          ...selectedItem,
          questionsSchema: updatedQuestionsSchema
        });
      }

      if (successMessage) {
        Alert.alert('Success', successMessage);
      }

      loadData();
    } catch (error) {
      console.error(errorLogMessage, error);
      Alert.alert('Error', errorAlertMessage);
    }
  };

  const handleDeactivateQuestion = async (itemId: number, fieldId: string) => {
    await updateQuestionFields(
      itemId,
      (fields) => fields.map((field) => {
        if (field.id === fieldId) {
          return {
            ...field,
            deprecated: true,
            deprecatedAt: new Date().toISOString()
          };
        }
        return field;
      }),
      'Error deactivating question:',
      'Failed to deactivate question field',
      'Question field deactivated successfully',
    );
  };

  const handleRestoreQuestion = async (itemId: number, fieldId: string) => {
    await updateQuestionFields(
      itemId,
      (fields) => fields.map((field) => {
        if (field.id === fieldId) {
          const { deprecated, deprecatedAt, ...restoredField } = field;
          return restoredField;
        }
        return field;
      }),
      'Error restoring question:',
      'Failed to restore question field',
      'Question field restored successfully',
    );
  };

  const handleToggleMultiplicity = async (itemId: number, fieldId: string, newValue: boolean) => {
    await updateQuestionFields(
      itemId,
      (fields) => fields.map((field) => (
        field.id === fieldId
          ? { ...field, triggersMultiplicity: newValue }
          : field
      )),
      'Error updating multiplicity setting:',
      'Failed to update multiplicity setting',
    );
  };

  const handleLocalReorderQuestion = (fieldId: string, direction: 'up' | 'down') => {
    const currentOrder = [...localQuestionOrder];
    const currentIndex = currentOrder.findIndex(field => field.id === fieldId);
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (targetIndex < 0 || targetIndex >= currentOrder.length) return;

    // Swap questions in local array
    [currentOrder[currentIndex], currentOrder[targetIndex]] = [currentOrder[targetIndex], currentOrder[currentIndex]];

    setLocalQuestionOrder(currentOrder);
    setHasQuestionOrderChanges(true);
  };

  const saveQuestionOrder = async () => {
    if (!selectedItem) return;

    try {
      // Update the questions schema with the new order
      const updatedQuestionsSchema = {
        ...selectedItem.questionsSchema,
        fields: localQuestionOrder
      };

      // Save to backend
      await adminService.updateItem(selectedItem.id, {
        questionsSchema: updatedQuestionsSchema
      });

      // Immediately update selectedItem to reflect the new order
      setSelectedItem({
        ...selectedItem,
        questionsSchema: updatedQuestionsSchema
      });

      // Update localQuestionOrder to keep it in sync (already has the right order)
      // but create a new array reference to ensure re-render
      setLocalQuestionOrder([...localQuestionOrder]);

      setHasQuestionOrderChanges(false);
      // No alert for smoother experience
      loadData(); // Refresh the data for consistency
    } catch (error) {
      console.error('Error saving question order:', error);
      Alert.alert('Error', 'Failed to save question order');
    }
  };

  const handleCreateItem = async (itemData: AdminItemPayload) => {
    try {
      await adminService.createItem(itemData);
      Alert.alert('Success', 'Item created successfully');
      setCreateItemModalVisible(false);
      loadData();
    } catch (error) {
      console.error('Error creating item:', error);
      Alert.alert('Error', 'Failed to create item');
    }
  };

  const closeCreateItemModal = () => {
    setCreateItemModalVisible(false);
    setCreateItemForm(DEFAULT_CREATE_ITEM_FORM);
  };

  const submitCreateItemModal = () => {
    if (createItemForm.name && createItemForm.categoryId) {
      const categoryId = parseInt(createItemForm.categoryId, 10);
      const categoryItems = items.filter(item => item.categoryId === categoryId);
      const nextDisplayOrder = categoryItems.length > 0
        ? Math.max(...categoryItems.map(item => item.displayOrder), 0) + 1
        : 1;

      const { findingMode, findingQuestionIds, ...baseForm } = createItemForm;
      handleCreateItem({
        ...baseForm,
        categoryId,
        displayOrder: nextDisplayOrder,
        questionsSchema: {
          fields: [],
          findingClassification: findingClassificationForAdminSave(
            undefined,
            findingMode,
            findingQuestionIds
          ),
        },
        requiresLocation: createItemForm.requiresLocation,
        requiresFloor: createItemForm.requiresFloor,
        showInHamburger: createItemForm.showInHamburger,
        hamburgerIcon: createItemForm.hamburgerIcon || null,
        hamburgerOrder: createItemForm.hamburgerOrder,
      });
      setCreateItemForm(DEFAULT_CREATE_ITEM_FORM);
    } else {
      Alert.alert('Error', 'Name and Category are required');
    }
  };

  const handleCreateCategory = async (categoryData: AdminCategoryPayload) => {
    try {
      // Convert empty parentCategoryId to null for API
      const dataToSend = {
        ...categoryData,
        parentCategoryId: categoryData.parentCategoryId && categoryData.parentCategoryId !== ''
          ? parseInt(String(categoryData.parentCategoryId), 10)
          : null
      };
      await adminService.createCategory(dataToSend);
      Alert.alert('Success', 'Category created successfully');
      setCreateCategoryModalVisible(false);
      loadData();
    } catch (error) {
      console.error('Error creating category:', error);
      Alert.alert('Error', 'Failed to create category');
    }
  };

  const closeCreateCategoryModal = () => {
    setCreateCategoryModalVisible(false);
    setCreateCategoryForm(DEFAULT_CREATE_CATEGORY_FORM);
  };

  const submitCreateCategoryModal = () => {
    if (createCategoryForm.name) {
      const nextDisplayOrder = categories.length > 0
        ? Math.max(...categories.map(category => category.displayOrder), 0) + 1
        : 1;

      handleCreateCategory({
        ...createCategoryForm,
        displayOrder: nextDisplayOrder,
        templateId: selectedTemplateId,
      });
      setCreateCategoryForm(DEFAULT_CREATE_CATEGORY_FORM);
    } else {
      Alert.alert('Error', 'Name is required');
    }
  };

  const handleUpdateItem = async (itemId: number, itemData: AdminItemPayload) => {
    try {
      await adminService.updateItem(itemId, itemData);
      Alert.alert('Success', 'Item updated successfully');
      setEditItemModalVisible(false);
      loadData();
    } catch (error) {
      console.error('Error updating item:', error);
      Alert.alert('Error', 'Failed to update item');
    }
  };

  const closeEditItemModal = () => {
    setEditItemModalVisible(false);
  };

  const openEditItemQuestionsModal = () => {
    setEditItemModalVisible(false);
    setModalVisible(true);
  };

  const submitEditItemModal = () => {
    if (!selectedItem) return;

    if (editItemForm.name && editItemForm.categoryId) {
      const { findingMode, findingQuestionIds, ...baseForm } = editItemForm;
      handleUpdateItem(selectedItem.id, {
        ...baseForm,
        categoryId: parseInt(editItemForm.categoryId, 10),
        requiresLocation: editItemForm.requiresLocation,
        requiresFloor: editItemForm.requiresFloor,
        showInHamburger: editItemForm.showInHamburger,
        hamburgerIcon: editItemForm.hamburgerIcon || null,
        hamburgerOrder: editItemForm.hamburgerOrder,
        questionsSchema: {
          ...selectedItem.questionsSchema,
          findingClassification: findingClassificationForAdminSave(
            selectedItem.questionsSchema?.findingClassification,
            findingMode,
            findingQuestionIds
          ),
        },
        // Keep existing displayOrder when editing
      });
    } else {
      Alert.alert('Error', 'Name and Category are required');
    }
  };

  const handleUpdateCategory = async (categoryId: number, categoryData: AdminCategoryPayload) => {
    try {
      // Convert empty parentCategoryId to null for API
      const dataToSend = {
        ...categoryData,
        parentCategoryId: categoryData.parentCategoryId && categoryData.parentCategoryId !== ''
          ? parseInt(String(categoryData.parentCategoryId), 10)
          : null
      };
      await adminService.updateCategory(categoryId, dataToSend);
      Alert.alert('Success', 'Category updated successfully');
      setEditCategoryModalVisible(false);
      loadData();
    } catch (error) {
      console.error('Error updating category:', error);
      Alert.alert('Error', 'Failed to update category');
    }
  };

  const closeEditCategoryModal = () => {
    setEditCategoryModalVisible(false);
  };

  const submitEditCategoryModal = async () => {
    if (!selectedCategory) return;

    if (editCategoryForm.name) {
      if (hasOrderChanges) {
        await saveItemOrder();
      }

      handleUpdateCategory(selectedCategory.id, {
        ...editCategoryForm,
        // Keep existing displayOrder when editing
      });
    } else {
      Alert.alert('Error', 'Name is required');
    }
  };

  const showAlert = (title: string, message: string, buttons: AlertButton[]) => {
    if (Platform.OS === 'web') {
      // For web, use browser's confirm dialog for simple yes/no
      if (buttons.length === 2 && buttons.some(b => b.style === 'destructive')) {
        const confirmed = window.confirm(`${title}\n\n${message}`);
        if (confirmed) {
          const deleteButton = buttons.find(b => b.style === 'destructive');
          if (deleteButton?.onPress) {
            deleteButton.onPress();
          }
        }
      } else if (buttons.length === 1) {
        // Simple OK alert
        window.alert(`${title}\n\n${message}`);
        if (buttons[0]?.onPress) {
          buttons[0].onPress();
        }
      } else {
        // For multiple choice, we'll need to use window.confirm for now
        // In the future, could implement a proper modal
        const confirmed = window.confirm(`${title}\n\n${message}`);
        if (confirmed && buttons[0]?.onPress) {
          buttons[0].onPress();
        }
      }
    } else {
      Alert.alert(title, message, buttons);
    }
  };

  const handleDeleteCategory = async (categoryId: number, moveToCategory?: number) => {
    try {
      await adminService.deleteCategory(categoryId, moveToCategory);
      showAlert('Success', 'Category deleted successfully', [{ text: 'OK' }]);
      loadData();
    } catch (error) {
      console.error('Error deleting category:', error);
      const apiError = error as ApiErrorLike;
      console.error('Error response:', apiError.response);
      const errorMessage = getApiErrorMessage(error, 'Failed to delete category');
      showAlert('Error', errorMessage, [{ text: 'OK' }]);
    }
  };

  // Template management functions
  const handleCreateTemplate = async () => {
    try {
      if (!templateForm.name.trim() || !templateForm.type.trim()) {
        Alert.alert('Error', 'Please fill in template name and type');
        return;
      }

      await adminService.createTemplate({
        name: templateForm.name.trim(),
        type: templateForm.type.trim().toLowerCase().replace(/\s+/g, '_'),
        description: templateForm.description.trim() || undefined
      });

      Alert.alert('Success', 'Template created successfully');
      setTemplateModalVisible(false);
      setTemplateForm(DEFAULT_TEMPLATE_FORM);
      loadTemplates();
    } catch (error) {
      console.error('Error creating template:', error);
      const message = getApiErrorMessage(error, 'Failed to create template');
      Alert.alert('Error', message);
    }
  };

  const handleEditTemplate = async () => {
    try {
      if (!selectedTemplate || !templateForm.name.trim()) {
        Alert.alert('Error', 'Please fill in template name');
        return;
      }

      await adminService.updateTemplate(selectedTemplate.id, {
        name: templateForm.name.trim(),
        description: templateForm.description.trim() || undefined
      });

      Alert.alert('Success', 'Template updated successfully');
      setTemplateModalVisible(false);
      setTemplateForm(DEFAULT_TEMPLATE_FORM);
      setSelectedTemplate(null);
      loadTemplates();
    } catch (error) {
      console.error('Error updating template:', error);
      const message = getApiErrorMessage(error, 'Failed to update template');
      Alert.alert('Error', message);
    }
  };

  const handleCloneTemplate = async () => {
    try {
      if (!selectedTemplate || !templateForm.name.trim() || !templateForm.type.trim()) {
        Alert.alert('Error', 'Please fill in template name and type');
        return;
      }

      await adminService.cloneTemplate(selectedTemplate.id, {
        name: templateForm.name.trim(),
        type: templateForm.type.trim().toLowerCase().replace(/\s+/g, '_'),
        description: templateForm.description.trim() || undefined
      });

      Alert.alert('Success', 'Template cloned successfully');
      setTemplateModalVisible(false);
      setTemplateForm(DEFAULT_TEMPLATE_FORM);
      setSelectedTemplate(null);
      loadTemplates();
    } catch (error) {
      console.error('Error cloning template:', error);
      const message = getApiErrorMessage(error, 'Failed to clone template');
      Alert.alert('Error', message);
    }
  };

  const handleDeleteTemplate = async (templateId: number) => {
    Alert.alert(
      'Confirm Delete',
      'Are you sure you want to delete this template? This will make it inactive.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              await adminService.deleteTemplate(templateId);
              Alert.alert('Success', 'Template deleted successfully');
              loadTemplates();
            } catch (error) {
              console.error('Error deleting template:', error);
              const message = getApiErrorMessage(error, 'Failed to delete template');
              Alert.alert('Error', message);
            }
          }
        }
      ]
    );
  };

  const openTemplateModal = (mode: 'create' | 'edit' | 'clone', template?: ReviewTemplate) => {
    setTemplateModalMode(mode);
    setSelectedTemplate(template || null);
    
    if (mode === 'create') {
      setTemplateForm(DEFAULT_TEMPLATE_FORM);
    } else if (mode === 'edit' && template) {
      setTemplateForm({ name: template.name, type: template.type, description: template.description || '' });
    } else if (mode === 'clone' && template) {
      setTemplateForm({ name: `${template.name} (Copy)`, type: `${template.type}_copy`, description: template.description || '' });
    }
    
    setTemplateModalVisible(true);
  };

  const closeTemplateModal = () => {
    setTemplateModalVisible(false);
    setTemplateForm(DEFAULT_TEMPLATE_FORM);
    setSelectedTemplate(null);
  };

  const submitTemplateModal = () => {
    if (templateModalMode === 'create') {
      handleCreateTemplate();
    } else if (templateModalMode === 'edit') {
      handleEditTemplate();
    } else {
      handleCloneTemplate();
    }
  };

  const closeVendorModal = () => {
    setVendorModalVisible(false);
    setSelectedTemplateForVendors(null);
    setAvailableVendors([]);
  };

  // Vendor management functions
  const handleManageVendors = async (template: ReviewTemplate) => {
    setLoadingVendors(true);
    setVendorModalVisible(true);
    
    try {
      // Load all vendors and template's vendors in parallel
      const [vendorsData, templateVendorsData] = await Promise.all([
        adminService.listVendors<Vendor>(),
        adminService.listTemplateVendors<Vendor>(template.id)
      ]);
      
      setAvailableVendors(vendorsData || []);
      
      // Update the template with its vendors
      const updatedTemplate = { ...template, vendors: templateVendorsData || [] };
      setSelectedTemplateForVendors(updatedTemplate);
    } catch (error) {
      console.error('Error loading vendors:', error);
      Alert.alert('Error', 'Failed to load vendors');
      setAvailableVendors([]);
      setSelectedTemplateForVendors(template);
    } finally {
      setLoadingVendors(false);
    }
  };

  const handleToggleVendorAssociation = async (vendorId: number, isAssociated: boolean) => {
    if (!selectedTemplateForVendors) return;
    
    try {
      if (isAssociated) {
        // Remove association
        await adminService.removeVendorFromTemplate(selectedTemplateForVendors.id, vendorId);
      } else {
        // Add association
        await adminService.addVendorToTemplate(selectedTemplateForVendors.id, vendorId, {
          isPrimary: false,
          notes: ''
        });
      }
      
      // Refresh template data to show updated associations
      await loadTemplates();
      
      // Update the selected template in the modal with fresh vendor data
      const templateVendorsData = await adminService.listTemplateVendors<Vendor>(selectedTemplateForVendors.id);
      const updatedTemplate = { ...selectedTemplateForVendors, vendors: templateVendorsData || [] };
      setSelectedTemplateForVendors(updatedTemplate);
    } catch (error) {
      console.error('Error updating vendor association:', error);
      Alert.alert('Error', 'Failed to update vendor association');
    }
  };

  const handleLocalReorderItem = (itemId: number, direction: 'up' | 'down') => {
    const currentOrder = [...localItemOrder];
    const currentIndex = currentOrder.findIndex(item => item.id === itemId);
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (targetIndex < 0 || targetIndex >= currentOrder.length) return;
    
    // Swap items in local array
    [currentOrder[currentIndex], currentOrder[targetIndex]] = [currentOrder[targetIndex], currentOrder[currentIndex]];
    
    setLocalItemOrder(currentOrder);
    setHasOrderChanges(true);
  };
  
  const saveItemOrder = async () => {
    try {
      // Update display orders based on new positions
      const updates = localItemOrder.map((item, index) => 
        adminService.updateItem(item.id, { displayOrder: index + 1 })
      );
      
      await Promise.all(updates);
      setHasOrderChanges(false);
      loadData(); // Refresh the main list
    } catch (error) {
      console.error('Error saving item order:', error);
      Alert.alert('Error', 'Failed to save item order');
    }
  };

  const handleReorderCategory = async (categoryId: number, direction: 'up' | 'down', parentCategoryId?: number) => {
    try {
      const category = categories.find(c => c.id === categoryId);
      if (!category) return;
      
      // Get sibling categories (same parent) sorted by display order
      const siblingCategories = categories
        .filter(c => c.parentCategoryId === parentCategoryId && c.templateId === category.templateId)
        .sort((a, b) => a.displayOrder - b.displayOrder);
      
      const currentIndex = siblingCategories.findIndex(c => c.id === categoryId);
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      
      if (targetIndex < 0 || targetIndex >= siblingCategories.length) return;
      
      // Swap display orders
      const targetCategory = siblingCategories[targetIndex];
      await Promise.all([
        adminService.updateCategory(category.id, { displayOrder: targetCategory.displayOrder }),
        adminService.updateCategory(targetCategory.id, { displayOrder: category.displayOrder })
      ]);
      
      loadData();
    } catch (error) {
      console.error('Error reordering category:', error);
      Alert.alert('Error', 'Failed to reorder category');
    }
  };
  
  const handleCreateQuestion = async () => {
    if (!selectedItem || !newQuestionForm.label) {
      Alert.alert('Error', 'Question label is required');
      return;
    }
    
    try {
      // Generate a unique ID for the new question
      const newQuestionId = `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Create the new question object
      const newQuestion: QuestionField = {
        id: newQuestionId,
        label: newQuestionForm.label,
        type: newQuestionForm.type,
        required: false,  // Never required, since we don't enforce required fields anyway
        deprecated: false
      };
      
      // Add options if it's a select, radio, or checkbox type
      if (['select', 'radio', 'checkbox'].includes(newQuestionForm.type) && newQuestionForm.options) {
        newQuestion.options = newQuestionForm.options.split(',').map(opt => opt.trim()).filter(opt => opt);
      }

      // Add photo field settings if it's a file type
      if (newQuestionForm.type === 'file') {
        newQuestion.multiple = newQuestionForm.multiple;
        if (newQuestionForm.multiple) {
          newQuestion.maxPhotos = newQuestionForm.maxPhotos;
        }
      }

      // Add triggersMultiplicity flag for number and plusminus fields
      if (newQuestionForm.type === 'number' || newQuestionForm.type === 'plusminus') {
        newQuestion.triggersMultiplicity = newQuestionForm.triggersMultiplicity;
      }

      // Add links if it's a helpful_links type
      if (newQuestionForm.type === 'helpful_links') {
        const validLinks = newQuestionForm.links.filter(link => link.url.trim() && link.label.trim());
        if (validLinks.length === 0) {
          Alert.alert('Error', 'At least one link with both URL and label is required');
          return;
        }
        newQuestion.links = validLinks;
      }

      // Add unit if it's a dimensions type
      if (newQuestionForm.type === 'dimensions_2d' || newQuestionForm.type === 'dimensions_3d') {
        newQuestion.unit = newQuestionForm.unit;
      }

      // Update the item's questions schema
      const updatedQuestionsSchema = {
        ...selectedItem.questionsSchema,
        fields: [...getQuestionFields(selectedItem), newQuestion]
      };
      
      // Save to backend
      await adminService.updateItem(selectedItem.id, {
        questionsSchema: updatedQuestionsSchema
      });

      // Update localQuestionOrder if the modal is still open
      // This ensures the questions list updates immediately without closing the modal
      if (modalVisible) {
        setLocalQuestionOrder([...updatedQuestionsSchema.fields]);
      }

      // Update the selectedItem with the new questions schema
      setSelectedItem({
        ...selectedItem,
        questionsSchema: updatedQuestionsSchema
      });

      Alert.alert('Success', 'Question added successfully');
      setNewQuestionModalVisible(false);
      setNewQuestionForm(createDefaultNewQuestionForm());
      loadData(); // Refresh data for consistency
    } catch (error) {
      console.error('Error creating question:', error);
      Alert.alert('Error', 'Failed to create question');
    }
  };

  const toggleCategoryExpansion = (categoryId: number) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const closeQuestionModal = () => {
    setModalVisible(false);
    setLocalQuestionOrder([]);
    setHasQuestionOrderChanges(false);
  };

  const openNewQuestionModal = () => {
    setNewQuestionModalVisible(true);
  };

  const closeNewQuestionModal = () => {
    setNewQuestionModalVisible(false);
    setNewQuestionForm(createDefaultNewQuestionForm());
  };

  // Update edit form when selectedCategory changes
  React.useEffect(() => {
    if (selectedCategory && editCategoryModalVisible) {
      setEditCategoryForm({
        name: selectedCategory.name,
        description: selectedCategory.description || '',
        icon: selectedCategory.icon,
        parentCategoryId: selectedCategory.parentCategoryId ? selectedCategory.parentCategoryId.toString() : '',
        showInHamburger: selectedCategory.showInHamburger || false,
        hamburgerIcon: selectedCategory.hamburgerIcon || null,
        hamburgerOrder: selectedCategory.hamburgerOrder || 0,
      });

      // Initialize local item order with current items in this category
      const categoryItems = items
        .filter(item => item.categoryId === selectedCategory.id)
        .sort((a, b) => a.displayOrder - b.displayOrder);
      setLocalItemOrder(categoryItems);
      setHasOrderChanges(false);
    }
  }, [selectedCategory, editCategoryModalVisible, items]);

  if (loading) {
    return (
      <View testID="admin-templates-screen" style={styles.loading}>
        <Text>Loading admin data...</Text>
      </View>
    );
  }

  // Add error state if items failed to load
  if (!items || !categories) {
    return (
      <View testID="admin-templates-screen" style={styles.loading}>
        <Text>Error loading data. Please refresh the page.</Text>
      </View>
    );
  }

  const visibleItems = items
    .filter(item => includeDeactivated || !item.isDeprecated)
    .sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <View testID="admin-templates-screen" style={styles.container}>
      <ScrollView testID="admin-templates-scroll" style={styles.scrollView}>
        <AdminDashboardHeader
          activeTab={activeTab}
          categoriesCount={categories?.length || 0}
          includeDeactivated={includeDeactivated}
          itemsCount={visibleItems.length}
          onBack={onBack}
          onChangeIncludeDeactivated={setIncludeDeactivated}
          onChangeTab={setActiveTab}
          onSelectTemplate={setSelectedTemplateId}
          selectedTemplateId={selectedTemplateId}
          sharingCount={allItemLinks?.length || 0}
          templates={templates}
          templatesCount={templates?.length || 0}
        />

        {activeTab === 'items' ? (
          <AdminItemsSection
            categories={categories}
            items={visibleItems}
            onCreateItem={() => setCreateItemModalVisible(true)}
            onDeactivateItem={handleDeactivateItem}
            onEditItem={(item) => {
              setSelectedItem(item);
              setEditItemModalVisible(true);
            }}
            onOpenQuestions={(item) => {
              setSelectedItem(item);
              setLocalQuestionOrder([...getQuestionFields(item)]);
              setHasQuestionOrderChanges(false);
              setModalVisible(true);
            }}
            onRestoreItem={handleRestoreItem}
            onShareItem={(item) => {
              setSelectedItemForSharing(item);
              setShareItemModalVisible(true);
            }}
          />
        ) : activeTab === 'categories' ? (
          <AdminCategoriesSection
            categories={categories}
            expandedCategoryIds={expandedCategories}
            items={items}
            onCreateCategory={() => setCreateCategoryModalVisible(true)}
            onDeleteCategory={handleDeleteCategory}
            onEditCategory={(category) => {
              setSelectedCategory(category);
              setEditCategoryModalVisible(true);
            }}
            onReorderCategory={handleReorderCategory}
            onShowAlert={showAlert}
            onToggleCategoryExpansion={toggleCategoryExpansion}
            selectedTemplateId={selectedTemplateId}
          />
        ) : activeTab === 'templates' ? (
          <AdminTemplatesSection
            templates={templates}
            onCreateTemplate={() => openTemplateModal('create')}
            onDeleteTemplate={handleDeleteTemplate}
            onManageVendors={handleManageVendors}
            onOpenTemplateModal={openTemplateModal}
          />
        ) : activeTab === 'reports' && selectedTemplateId !== null ? (
          <AdminCumulativeReportsSection
            categories={categories}
            items={visibleItems}
            template={templates.find(template => template.id === selectedTemplateId)!}
            onTemplateUpdated={handleCumulativeReportTemplateUpdated}
          />
        ) : (
          <AdminSharingLinksSection
            links={allItemLinks}
            templates={templates}
            onUnlinkItems={handleUnlinkItems}
          />
        )}
      </ScrollView>

      <AdminQuestionModal
        visible={modalVisible}
        item={selectedItem}
        localQuestionOrder={localQuestionOrder}
        hasQuestionOrderChanges={hasQuestionOrderChanges}
        onDismiss={closeQuestionModal}
        onAddQuestion={openNewQuestionModal}
        onDeactivateQuestion={handleDeactivateQuestion}
        onRestoreQuestion={handleRestoreQuestion}
        onToggleMultiplicity={handleToggleMultiplicity}
        onReorderQuestion={handleLocalReorderQuestion}
        onSaveQuestionOrder={saveQuestionOrder}
      />
      <AdminNewQuestionModal
        visible={newQuestionModalVisible}
        form={newQuestionForm}
        onChangeForm={setNewQuestionForm}
        onDismiss={closeNewQuestionModal}
        onSubmit={handleCreateQuestion}
      />
      <AdminCreateItemModal
        visible={createItemModalVisible}
        form={createItemForm}
        categories={categories}
        categoryMenuVisible={createCategoryMenuVisible}
        selectedTemplateId={selectedTemplateId}
        onChangeForm={setCreateItemForm}
        onChangeCategoryMenuVisible={setCreateCategoryMenuVisible}
        onDismiss={closeCreateItemModal}
        onSubmit={submitCreateItemModal}
      />
      <AdminCreateCategoryModal
        visible={createCategoryModalVisible}
        form={createCategoryForm}
        categories={categories}
        parentMenuVisible={createParentCategoryMenuVisible}
        selectedTemplateId={selectedTemplateId}
        onChangeForm={setCreateCategoryForm}
        onChangeParentMenuVisible={setCreateParentCategoryMenuVisible}
        onDismiss={closeCreateCategoryModal}
        onSubmit={submitCreateCategoryModal}
      />
      <AdminEditItemModal
        visible={editItemModalVisible}
        item={selectedItem}
        form={editItemForm}
        categories={categories}
        categoryMenuVisible={editCategoryMenuVisible}
        selectedTemplateId={selectedTemplateId}
        onChangeForm={setEditItemForm}
        onChangeCategoryMenuVisible={setEditCategoryMenuVisible}
        onDismiss={closeEditItemModal}
        onEditQuestions={openEditItemQuestionsModal}
        onSubmit={submitEditItemModal}
      />
      <AdminEditCategoryModal
        visible={editCategoryModalVisible}
        category={selectedCategory}
        form={editCategoryForm}
        categories={categories}
        localItemOrder={localItemOrder}
        hasOrderChanges={hasOrderChanges}
        parentMenuVisible={editParentCategoryMenuVisible}
        selectedTemplateId={selectedTemplateId}
        onChangeForm={setEditCategoryForm}
        onChangeParentMenuVisible={setEditParentCategoryMenuVisible}
        onDismiss={closeEditCategoryModal}
        onReorderItem={handleLocalReorderItem}
        onSaveItemOrder={saveItemOrder}
        onSubmit={submitEditCategoryModal}
      />
      <AdminTemplateModal
        visible={templateModalVisible}
        mode={templateModalMode}
        form={templateForm}
        onChangeForm={setTemplateForm}
        onDismiss={closeTemplateModal}
        onSubmit={submitTemplateModal}
      />
      <AdminVendorModal
        visible={vendorModalVisible}
        template={selectedTemplateForVendors}
        availableVendors={availableVendors}
        loading={loadingVendors}
        onDismiss={closeVendorModal}
        onToggleVendorAssociation={handleToggleVendorAssociation}
      />

      {/* Sharing Modal */}
      {selectedItemForSharing && (
        <ShareItemModal
          visible={shareItemModalVisible}
          onDismiss={() => {
            setShareItemModalVisible(false);
            setSelectedItemForSharing(null);
          }}
          sourceItem={selectedItemForSharing as any}
          sourceTemplateId={selectedTemplateId!}
          templates={templates}
          onSuccess={() => {
            // Reload data to show new sharing relationships
            loadData();
          }}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scrollView: {
    flex: 1,
    padding: spacing.md,
  },
  loading: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default AdminDashboard;
